import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FiDownload, FiMail, FiChevronDown, FiCheck, FiFileText, FiEye } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import Stock from '../../../model/Stock';
import { AnalyticsResult } from '../../../model/AnalyticsResult';
import { toast } from '../Toast';
import { displayName } from '../Header';
import { ReportModal } from '../shareRegistry/ReportModals';
import {
    SECTIONS, DEFAULT_SECTIONS, SectionKey, quarterLabel, quarterRange, analyticsReportUrl, reportPages, reportFileName,
} from './analyticsExport';

// Figma 460:4494 "Export analytics report" pop-up, then 482:1434 "Export ready".
// PDF opens the printable investor report (AnalyticsReportView, real Analytics data) in a new tab; Excel, emailing
// and the monthly schedule have no backend yet, so they are preview only.

type Format = 'pdf' | 'excel';

interface Props { stock: Stock; data: AnalyticsResult; onClose: () => void }

const AnalyticsExportModal: React.FC<Props> = ({ stock, data, onClose }) => {
    const user = useSelector((state: IGlobalState) => state.user);
    const [format, setFormat] = useState<Format>('pdf');
    const [quarter, setQuarter] = useState(data.quarter);
    const [sections, setSections] = useState<SectionKey[]>(DEFAULT_SECTIONS);
    const [ready, setReady] = useState(false);
    const company = stock.companyName || stock.ticker;

    const toggle = (key: SectionKey) =>
        setSections(s => s.includes(key) ? s.filter(x => x !== key) : SECTIONS.map(x => x.key).filter(k => k === key || s.includes(k)));

    const emailMe = () => toast.info('Preview only: emailing the report isn’t connected yet — download the PDF and attach it for now.');

    const download = () => {
        if (format === 'excel') { toast.info('Preview only: the Excel export isn’t built yet — pick PDF for now.'); return; }
        setReady(true);
    };

    // the ready state has its own overlay (no header), so it needs its own Escape handler
    useEffect(() => {
        if (!ready) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [ready, onClose]);

    if (ready) {
        const pages = reportPages(sections).length;
        const open = (print: boolean) => window.open(analyticsReportUrl(stock, quarter, sections, print), '_blank', 'noopener');
        return <div className="campaign-modal-overlay" onClick={onClose}>
            <div className="campaign-modal analytics-export-modal ready" role="dialog" aria-modal="true" aria-label="Your board report is ready" onClick={e => e.stopPropagation()}>
                <div className="aer-body">
                    <span className="aer-tick"><FiCheck /></span>
                    <h2>Your board report is ready</h2>
                    <p className="aer-meta">{company} · {quarterRange(quarter, quarter === data.quarters[0])} · {pages} page{pages === 1 ? '' : 's'} · PDF</p>
                    <div className="aer-file">
                        <span className="icon-tile"><FiFileText /></span>
                        <div className="aer-file-t">
                            <strong>{reportFileName(stock, quarter)}</strong>
                            <span>Generated just now by {displayName(user)} · opens in a new tab to print or save as PDF</span>
                        </div>
                    </div>
                    <div className="aer-acts">
                        <button type="button" className="btn-outline" onClick={() => open(false)}><FiEye /> Open the Report</button>
                        <button type="button" className="btn-outline" onClick={emailMe}><FiMail /> Email to the Board</button>
                        <button type="button" className="btn-primary" onClick={() => open(true)}><FiDownload /> Download PDF</button>
                    </div>
                </div>
                <div className="aer-footer">
                    <span>Want this every month?{' '}
                        <button type="button" className="link" onClick={() => toast.info('Preview only: scheduled reports aren’t connected yet.')}>Set up a monthly email</button>
                    </span>
                    <button type="button" className="btn-cancel" onClick={onClose}>Done</button>
                </div>
            </div>
        </div>;
    }

    return <ReportModal className="analytics-export-modal" icon={<FiDownload />} title="Export Analytics Report"
        sub="A board‑ready PDF or the raw numbers in Excel" note="" onClose={onClose}
        buttons={<>
            <button type="button" className="btn-outline" onClick={emailMe}><FiMail /> Email It to Me</button>
            <button type="button" className="btn-primary" onClick={download}><FiDownload /> Download</button>
        </>}>
        <div className="ae-fields">
            <label className="report-modal-field">
                <span>Format</span>
                <span className="report-modal-select">
                    <select value={format} onChange={e => setFormat(e.target.value as Format)}>
                        <option value="pdf">PDF (board pack style)</option>
                        <option value="excel">Excel (raw numbers) — preview only</option>
                    </select>
                    <FiChevronDown />
                </span>
            </label>
            <label className="report-modal-field">
                <span>Period</span>
                <span className="report-modal-select">
                    <select value={quarter} onChange={e => setQuarter(e.target.value)}>
                        {data.quarters.map((q, i) => <option key={q} value={q}>{quarterLabel(q)}{i === 0 ? ' (to date)' : ''}</option>)}
                    </select>
                    <FiChevronDown />
                </span>
            </label>
        </div>
        <span className="ae-caption">Include</span>
        <div className="ae-checks">
            {SECTIONS.map(s => {
                const on = sections.includes(s.key);
                return <button type="button" key={s.key} className={`ae-check${on ? ' on' : ''}`} role="checkbox" aria-checked={on} onClick={() => toggle(s.key)}>
                    <span className="ae-box">{on && <FiCheck />}</span>
                    <span>{s.title}</span>
                </button>;
            })}
        </div>
    </ReportModal>;
};

export default AnalyticsExportModal;
